import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { StyleContext } from "../../providers/StyleContextProvider";
import { AuthContext } from "../../providers/AuthContextProvider";
import { LoggedAdmin } from "../../models/LoggedAdmin";
import { useMediaQuery } from "react-responsive";
import useErrorPage from "../../hooks/useErrorPage";
import useAdminLoginRedirect from "../../hooks/useAdminLoginRedirect";
import { Option } from "../../models/Option";
import { Degree } from "../../models/Degree";
import { Header } from "../../components/shared/Header";
import { Select } from "../../components/shared/Select";
import { getPrivateDegree,editPrivateDegree } from "../../services/DegreeService";
import { Input } from "../../components/shared/inputs/Input";
import { QuillEditor } from "../../components/shared/QuillEditor";
import { Button } from "../../components/shared/Button";
import { PopUpDialog } from "../../components/shared/dialogs/PopUpDialog";
import { ResponseModel } from "../../models/ResponseModel";

export const PrivateDegreeEditPage: React.FC = () => {   
    let params = useParams(); 
    const navigate = useNavigate();
    const degreeId: number = +params.degreeId!;        
    const styleContext = useContext(StyleContext);
    const [styles, setStyles] = useState<any>(styleContext.getComponentStyle("degreeEditPage")); 
    const authContext = useContext(AuthContext);     
    const admin: LoggedAdmin | null = authContext.getLoggedAdmin();  
    const [categories, setCategories] = useState<Option[]>([]);
    const [universities, setUniversities] = useState<Option[]>([]);
    const [degreePeriods, setDegreePeriods] = useState<Option[]>([]);
    const [degree, setDegree] = useState<Degree>();
    const [title, setTitle] = useState<string>();
    const [subTitle, setSubTitle] = useState<string>();
    const [loading, setLoading] = useState<boolean>(true); 
    const [openDialog, setOpenDialog] = useState<boolean>(false);
    const isMobile = useMediaQuery({ query: "(max-width: 786px)" });
    const [toErrorPage] = useErrorPage();
    const [toAdminLogin] = useAdminLoginRedirect();

    useEffect(() => {
        if(admin === null) {
            toAdminLogin("Please login to access this page.");
        }
        else {
            getDegree();
        }
    }, []);
    
    useEffect(() => {        
        setStyles(styleContext.getComponentStyle("degreeEditPage")); 
    }, [isMobile]);   

    async function getDegree() {                
        await getPrivateDegree(degreeId, admin!.adminId, admin!.token)
        .then((data: any) => {
            setCategories(data.categories);
            setUniversities(data.universities);
            setDegreePeriods(data.degreePeriods);   
            setDegree(data.degree);
            setTitle(data.degree.degreeName_SIN);
            setSubTitle(data.degree.degreeName_ENG);    
            setLoading(false);
        })
        .catch((error) => {            
            toErrorPage(error);
        });                       
    }    

    const handleUniversityChange = (universityId: number) => {        
        setDegree((values: any) => ({ ...values, ["privateUniversityId"]: universityId }));         
    }  

    const handleDegreePeriodChange = (degreePeriodId: number) => {        
        setDegree((values: any) => ({ ...values, ["privateDegreePeriodId"]: degreePeriodId }));              
    }

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value;
        setDegree((values: any) => ({ ...values, [name]: value }));
    }

    const handleContentChange = (name: string, content: string) => {        
        setDegree((values: any) => ({ ...values, [name]: content }));
    }

    async function postEditResponse () {            
        await editPrivateDegree(degree!, admin!.adminId, admin!.token)
        .then((response: ResponseModel) => {                
            setOpenDialog(true);
        })
        .catch((error) => {            
            toErrorPage(error);
        });                               
    }

    const closeDialog = () => {
        setOpenDialog(false);   
        navigate(-1);     
    }

    return (
        <>
            <Header /> 
            {loading ? <></> : <div style={styles}>
                <div style={styles.titleWrapper}>
                    <h2 style={styles.title}>{title}</h2>
                    <h3 style={styles.title}>{subTitle}</h3>
                </div>                
                <div style={styles.rowWrapper}>
                    <div>
                        <label style={styles.label}>{"Degree Name [SIN]"}</label>
                        <Input
                            type={"text"}
                            name={"degreeName_SIN"}
                            value={degree?.degreeName_SIN}
                            styles={styles.input}
                            onChange={handleInputChange}                                                                                    
                        />
                    </div>
                    <div>
                        <label style={styles.label}>{"Degree Name [TAM]"}</label>
                        <Input
                            type={"text"}
                            name={"degreeName_TAM"}
                            value={degree?.degreeName_TAM}
                            styles={styles.input}
                            onChange={handleInputChange}                                                                                    
                        />
                    </div>
                    <div>
                        <label style={styles.label}>{"Degree Name [ENG]"}</label>
                        <Input
                            type={"text"}
                            name={"degreeName_ENG"}
                            value={degree?.degreeName_ENG}
                            styles={styles.input}
                            onChange={handleInputChange}                                                                                    
                        />
                    </div>
                </div>
                <div style={styles.rowWrapper}>
                    <div>
                        <label style={styles.label}>{"Categories"}</label>
                        <Select 
                            styles={styles.searchSelect}
                            label={"Categories"} 
                            options={categories}                             
                            getSelectedValue={() => {}} 
                        />
                    </div>
                    <div>
                        <label style={styles.label}>{"University"}</label>
                        <Select 
                            styles={styles.searchSelect}
                            label={"University"} 
                            options={universities} 
                            initialValue={degree?.privateUniversityId?.toString()}
                            getSelectedValue={handleUniversityChange} 
                        />
                    </div>                    
                    <div>
                        <label style={styles.label}>{"Degree Period"}</label>                   
                        <Select 
                            styles={styles.searchSelect}
                            label={"Degree Period"} 
                            options={degreePeriods} 
                            initialValue={degree?.privateDegreePeriodId?.toString()}
                            searchDisabled={true}
                            getSelectedValue={handleDegreePeriodChange} 
                        />
                    </div>
                </div>
                <div style={styles.rowWrapper}>
                    <div>
                        <label style={styles.label}>{"Degree Detail [SIN]"}</label>
                        <QuillEditor
                            name={"degreeDetail_SIN"}
                            value={degree?.degreeDetail_SIN} 
                            onChange={handleContentChange}
                            styles={styles.editor}
                        /> 
                    </div>
                    <div>
                        <label style={styles.label}>{"Degree Detail [TAM]"}</label>
                        <QuillEditor
                            name={"degreeDetail_TAM"}
                            value={degree?.degreeDetail_TAM} 
                            onChange={handleContentChange}
                            styles={styles.editor}
                        /> 
                    </div>
                    <div>
                        <label style={styles.label}>{"Degree Detail [ENG]"}</label>
                        <QuillEditor
                            name={"degreeDetail_ENG"}
                            value={degree?.degreeDetail_ENG} 
                            onChange={handleContentChange}
                            styles={styles.editor}
                        /> 
                    </div>
                </div>  
                <div style={styles.rowWrapper}>
                    <div>
                        <label style={styles.label}>{"Degree Content [SIN]"}</label>
                        <QuillEditor
                            name={"degreeContent_SIN"}
                            value={degree?.degreeContent_SIN} 
                            onChange={handleContentChange}
                            styles={styles.editor}
                        /> 
                    </div>
                    <div>
                        <label style={styles.label}>{"Degree Content [TAM]"}</label>
                        <QuillEditor
                            name={"degreeContent_TAM"}
                            value={degree?.degreeContent_TAM} 
                            onChange={handleContentChange}
                            styles={styles.editor}
                        /> 
                    </div>
                    <div>
                        <label style={styles.label}>{"Degree Content [ENG]"}</label>
                        <QuillEditor
                            name={"degreeContent_ENG"}
                            value={degree?.degreeContent_ENG} 
                            onChange={handleContentChange}
                            styles={styles.editor}
                        /> 
                    </div>
                </div>
                <div style={styles.rowWrapper}>
                    <div>
                        <label style={styles.label}>{"Job Opportunities [SIN]"}</label>
                        <QuillEditor
                            name={"jobOpportunities_SIN"}
                            value={degree?.jobOpportunities_SIN} 
                            onChange={handleContentChange}
                            styles={styles.editor}
                        /> 
                    </div>
                    <div>
                        <label style={styles.label}>{"Job Opportunities [TAM]"}</label>
                        <QuillEditor
                            name={"jobOpportunities_TAM"}
                            value={degree?.jobOpportunities_TAM} 
                            onChange={handleContentChange}
                            styles={styles.editor}
                        /> 
                    </div>
                    <div>
                        <label style={styles.label}>{"Job Opportunities [ENG]"}</label>
                        <QuillEditor
                            name={"jobOpportunities_ENG"}
                            value={degree?.jobOpportunities_ENG} 
                            onChange={handleContentChange}
                            styles={styles.editor}
                        /> 
                    </div>
                </div>       
                <div style={styles.buttonWrapper}>
                    <Button style={styles.submitButton} onClick={postEditResponse}>{'Submit'}</Button>
                </div>
            </div>}
            <PopUpDialog styles={styles.responseDialog} open={openDialog} toggle={closeDialog}>
                <h3 style={styles.responseTitle}>{"Success"}</h3>
                <p style={styles.responseMessage}>{"The degree details are submitted successfully."}</p>                
                <Button style={styles.responseButton} type="button" onClick={closeDialog}>{"OK"}</Button>    
            </PopUpDialog>
        </>
    )
}